import { AnimatePresence, motion } from "framer-motion";
import { useMemo } from "react";
import { useColorPulse, PulseEffect } from "@/context/color-pulse";

function hexToRgba(hex: string, alpha: number) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const bigint = parseInt(full, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Full-screen, pointer-events-none layer sitting behind all page content.
 *
 * Two systems live here:
 *  - OptionsCloud: while a quiz round is on screen, a slow drifting blend of
 *    the current round's option colors mixes together like a lava lamp / sky
 *    of clouds, so the background is already "in motion" while you decide.
 *  - EffectLayer: the instant you pick a color, one of many distinct
 *    animated effects fires once, then hands back to the next round's cloud.
 */
export function BackgroundFX() {
  const { pulse, optionColors } = useColorPulse();

  return (
    <div className="fixed inset-0 -z-10 pointer-events-none overflow-hidden bg-background">
      <AnimatePresence mode="sync">
        {optionColors.length > 0 && (
          <motion.div
            key={optionColors.join('-')}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: 'easeInOut' }}
          >
            <OptionsCloud colors={optionColors} />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {pulse && <EffectLayer key={pulse.key} hex={pulse.hex} effect={pulse.effect} />}
      </AnimatePresence>
    </div>
  );
}

function OptionsCloud({ colors }: { colors: string[] }) {
  const blobs = useMemo(
    () =>
      colors.map((hex, i) => {
        const angle = (i / colors.length) * Math.PI * 2;
        return {
          hex,
          left: 50 + Math.cos(angle) * 26,
          top: 50 + Math.sin(angle) * 26,
          duration: 16 + i * 4,
          driftX: 18 + (i % 3) * 10,
          driftY: 14 + (i % 2) * 12,
        };
      }),
    [colors.join('-')],
  );

  return (
    <div className="absolute inset-0">
      {blobs.map((b, i) => (
        <motion.div
          key={`${b.hex}-${i}`}
          className="absolute rounded-full blur-[110px]"
          style={{
            left: `${b.left}%`,
            top: `${b.top}%`,
            width: '58vmax',
            height: '58vmax',
            background: hexToRgba(b.hex, 0.32),
            translateX: '-50%',
            translateY: '-50%',
          }}
          animate={{
            x: [0, b.driftX, -b.driftX * 0.6, 0],
            y: [0, -b.driftY, b.driftY * 0.7, 0],
            scale: [1, 1.12, 0.94, 1],
          }}
          transition={{ duration: b.duration, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}

function EffectLayer({ hex, effect }: { hex: string; effect: PulseEffect }) {
  // Every randomized field an effect might need is computed unconditionally
  // (Rules of Hooks) even though only one effect actually renders.
  const rand = useMemo(
    () => ({
      orbs: Array.from({ length: 6 }).map(() => ({ left: Math.random() * 100, delay: Math.random() * 0.4, size: 80 + Math.random() * 160 })),
      bokeh: Array.from({ length: 10 }).map(() => ({ left: Math.random() * 100, top: Math.random() * 100, size: 40 + Math.random() * 120, delay: Math.random() })),
      plasma: Array.from({ length: 4 }).map(() => ({ left: 20 + Math.random() * 60, top: 20 + Math.random() * 60, size: 120 + Math.random() * 160, delay: Math.random() * 0.4 })),
      stars: Array.from({ length: 44 }).map(() => ({ angle: Math.random() * 360, distance: 30 + Math.random() * 55, delay: Math.random() * 0.3 })),
      rain: Array.from({ length: 26 }).map(() => ({ left: Math.random() * 100, delay: Math.random() * 0.6, duration: 1 + Math.random() * 0.8 })),
      fireflies: Array.from({ length: 20 }).map(() => ({ left: Math.random() * 100, top: Math.random() * 100, delay: Math.random() * 2, duration: 3 + Math.random() * 2 })),
      comets: Array.from({ length: 4 }).map(() => ({ top: Math.random() * 80, delay: Math.random() * 0.5 })),
      confetti: Array.from({ length: 32 }).map(() => ({ left: Math.random() * 100, delay: Math.random() * 0.4, duration: 1.4 + Math.random() * 0.8, rotate: Math.random() * 360 })),
      sweepAngle: 20 + Math.random() * 40,
      sweepFromLeft: Math.random() > 0.5,
    }),
    [],
  );

  switch (effect) {
    case 'burst':
      return (
        <motion.div
          className="absolute left-1/2 top-1/2 rounded-full"
          style={{ background: `radial-gradient(circle, ${hexToRgba(hex, 0.5)}, ${hexToRgba(hex, 0)} 70%)` }}
          initial={{ width: 0, height: 0, x: '-50%', y: '-50%', opacity: 0.9 }}
          animate={{ width: '180vmax', height: '180vmax', opacity: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.6, ease: 'easeOut' }}
        />
      );

    case 'sweep':
      return (
        <motion.div
          className="absolute -inset-y-1/2 w-1/3"
          style={{ background: `linear-gradient(${rand.sweepAngle}deg, transparent, ${hexToRgba(hex, 0.4)}, transparent)` }}
          initial={{ x: rand.sweepFromLeft ? '-60vw' : '60vw', opacity: 0.9 }}
          animate={{ x: rand.sweepFromLeft ? '120vw' : '-120vw', opacity: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.3, ease: 'easeInOut' }}
        />
      );

    case 'ripple':
      return (
        <>
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="absolute left-1/2 top-1/2 rounded-full border-2"
              style={{ borderColor: hexToRgba(hex, 0.5), x: '-50%', y: '-50%' } as any}
              initial={{ width: 0, height: 0, opacity: 0.8 }}
              animate={{ width: '140vmax', height: '140vmax', opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.8, delay: i * 0.25, ease: 'easeOut' }}
            />
          ))}
        </>
      );

    case 'orbs':
      return (
        <>
          {rand.orbs.map((o, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full blur-3xl"
              style={{ left: `${o.left}%`, width: o.size, height: o.size, background: hexToRgba(hex, 0.35) }}
              initial={{ bottom: '-10%', opacity: 0.8 }}
              animate={{ bottom: '110%', opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2.4, delay: o.delay, ease: 'easeOut' }}
            />
          ))}
        </>
      );

    case 'wave':
      return (
        <>
          {Array.from({ length: 6 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute left-0 right-0 h-6 blur-md"
              style={{ top: `${(i / 6) * 100}%`, background: hexToRgba(hex, 0.3) }}
              initial={{ x: '-100%', opacity: 0 }}
              animate={{ x: ['-100%', '100%'], opacity: [0, 0.8, 0] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2.2, delay: i * 0.08, ease: 'easeInOut' }}
            />
          ))}
        </>
      );

    case 'spiral':
      return (
        <motion.div
          className="absolute left-1/2 top-1/2 rounded-full"
          style={{
            width: '160vmax',
            height: '160vmax',
            x: '-50%',
            y: '-50%',
            background: `conic-gradient(from 0deg, ${hexToRgba(hex, 0.35)}, transparent 30%, ${hexToRgba(hex, 0.35)} 60%, transparent 90%)`,
          }}
          initial={{ rotate: 0, scale: 0, opacity: 0.9 }}
          animate={{ rotate: 200, scale: 1, opacity: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 2, ease: 'easeInOut' }}
        />
      );

    case 'vortex':
      return (
        <motion.div
          className="absolute left-1/2 top-1/2 rounded-full"
          style={{
            width: '160vmax',
            height: '160vmax',
            x: '-50%',
            y: '-50%',
            background: `conic-gradient(from 0deg, ${hexToRgba(hex, 0.4)}, transparent 25%, ${hexToRgba(hex, 0.4)} 50%, transparent 75%, ${hexToRgba(hex, 0.4)})`,
          }}
          initial={{ rotate: 0, scale: 1.4, opacity: 0.9 }}
          animate={{ rotate: -240, scale: 0, opacity: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 2, ease: 'easeIn' }}
        />
      );

    case 'kaleidoscope':
      return (
        <motion.div
          className="absolute left-1/2 top-1/2"
          style={{ width: '130vmax', height: '130vmax', x: '-50%', y: '-50%' }}
          initial={{ rotate: 0, opacity: 0.9 }}
          animate={{ rotate: 300, opacity: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 2.4, ease: 'easeInOut' }}
        >
          {[0, 60, 120, 180, 240, 300].map((deg) => (
            <div
              key={deg}
              className="absolute inset-0"
              style={{ background: `conic-gradient(from ${deg}deg, ${hexToRgba(hex, 0.3)}, transparent 15%)` }}
            />
          ))}
        </motion.div>
      );

    case 'aurora':
      return (
        <>
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="absolute inset-y-0 blur-2xl"
              style={{
                left: `${15 + i * 28}%`,
                width: '32%',
                background: `linear-gradient(180deg, transparent, ${hexToRgba(hex, 0.4)}, transparent)`,
                transform: 'skewX(-15deg)',
              }}
              initial={{ opacity: 0, y: '12%' }}
              animate={{ opacity: [0, 0.8, 0], y: ['12%', '-12%', '12%'] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2.6, delay: i * 0.2, ease: 'easeInOut' }}
            />
          ))}
        </>
      );

    case 'plasma':
      return (
        <>
          {rand.plasma.map((p, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full blur-3xl"
              style={{ left: `${p.left}%`, top: `${p.top}%`, width: p.size, height: p.size, background: hexToRgba(hex, 0.4) }}
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: [0.4, 1.3, 0.8], opacity: [0, 0.7, 0] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2, delay: p.delay, ease: 'easeInOut' }}
            />
          ))}
        </>
      );

    case 'bokeh':
      return (
        <>
          {rand.bokeh.map((b, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full blur-2xl"
              style={{ left: `${b.left}%`, top: `${b.top}%`, width: b.size, height: b.size, background: hexToRgba(hex, 0.32) }}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: [0, 0.8, 0], scale: [0.6, 1, 0.6] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.8, delay: b.delay, ease: 'easeInOut' }}
            />
          ))}
        </>
      );

    case 'starfield':
      return (
        <>
          {rand.stars.map((s, i) => {
            const rad = (s.angle * Math.PI) / 180;
            const dx = Math.cos(rad) * s.distance;
            const dy = Math.sin(rad) * s.distance;
            return (
              <motion.div
                key={i}
                className="absolute left-1/2 top-1/2 rounded-full"
                style={{ width: 3, height: 3, background: hex }}
                initial={{ x: '-50%', y: '-50%', opacity: 0.9 }}
                animate={{ x: [`-50%`, `calc(-50% + ${dx}vmax)`], y: [`-50%`, `calc(-50% + ${dy}vmax)`], opacity: [0.9, 0] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.4, delay: s.delay, ease: 'easeOut' }}
              />
            );
          })}
        </>
      );

    case 'rain':
      return (
        <>
          {rand.rain.map((r, i) => (
            <motion.div
              key={i}
              className="absolute top-0 w-0.5"
              style={{ left: `${r.left}%`, height: '18%', background: `linear-gradient(180deg, transparent, ${hexToRgba(hex, 0.6)})` }}
              initial={{ y: '-20%', opacity: 0 }}
              animate={{ y: '120vh', opacity: [0, 1, 0] }}
              exit={{ opacity: 0 }}
              transition={{ duration: r.duration, delay: r.delay, ease: 'linear' }}
            />
          ))}
        </>
      );

    case 'confetti':
      return (
        <>
          {rand.confetti.map((c, i) => (
            <motion.div
              key={i}
              className="absolute top-0 w-2 h-3"
              style={{ left: `${c.left}%`, background: hexToRgba(hex, 0.75) }}
              initial={{ y: '-5%', opacity: 0, rotate: 0 }}
              animate={{ y: '110vh', opacity: [0, 1, 1, 0], rotate: c.rotate }}
              exit={{ opacity: 0 }}
              transition={{ duration: c.duration, delay: c.delay, ease: 'easeIn' }}
            />
          ))}
        </>
      );

    case 'comet':
      return (
        <>
          {rand.comets.map((cm, i) => (
            <motion.div
              key={i}
              className="absolute h-0.5 w-40 blur-sm"
              style={{ top: `${cm.top}%`, background: `linear-gradient(90deg, transparent, ${hexToRgba(hex, 0.85)})` }}
              initial={{ x: '-20vw', opacity: 0, rotate: -18 }}
              animate={{ x: '120vw', opacity: [0, 1, 0] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.3, delay: cm.delay, ease: 'easeIn' }}
            />
          ))}
        </>
      );

    case 'fireflies':
    default:
      return (
        <>
          {rand.fireflies.map((f, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full"
              style={{
                left: `${f.left}%`,
                top: `${f.top}%`,
                width: 4,
                height: 4,
                background: hex,
                boxShadow: `0 0 8px 2px ${hexToRgba(hex, 0.8)}`,
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0.3, 1, 0], y: [0, -10, 0, 10, 0] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2.4, delay: f.delay, ease: 'easeInOut' }}
            />
          ))}
        </>
      );
  }
}
